import React, { forwardRef, useCallback } from 'react';

import { useSlug } from '../hooks/useSlug';
import { GlobalProps } from '../types';
import { classes, makeSrcset } from '../utils';

export type ImageProps = GlobalProps & {
  isLazy?: boolean;
  src: string;
  srcSet?: string;
  altText?: string;
  origWidth?: number;
  origHeight?: number;
  aspectRatio?: number;
  width?: number;
  height?: number;
  sizes?: string;
  onError?: (ev: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  onLoad?: (ev: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  srcSetScale?: number;
  fetchpriority?: 'high' | 'low' | 'auto';
  decoding?: 'async' | 'sync' | 'auto';
  useResponsivePNG?: boolean;
};

export const DEFAULT_NATIVE_WIDTH = 1200;

export const Image = forwardRef<HTMLImageElement, ImageProps>(
  function ImageComponent(props, ref) {
    const {
      isLazy = true,
      altText = '',
      name = 'image',
      src,
      srcSet,
      origWidth,
      origHeight,
      aspectRatio,
      width,
      height,
      onLoad,
      onError,
      sizes,
      srcSetScale = 1,
      fetchpriority,
      decoding,
      useResponsivePNG,
    } = props;

    let renderedWidth = width || origWidth || DEFAULT_NATIVE_WIDTH;
    let renderedHeight = height || -1;

    const slugName = useSlug(name);
    const cx = classes(['ui-image'], props);

    if (aspectRatio && height) {
      renderedWidth = Math.round(renderedHeight * aspectRatio);
      // Check that the ratio isn't pushing outside the max height
      // If it is, further crop the width
      if (origWidth && renderedWidth > origWidth) {
        renderedWidth = origWidth;
        renderedHeight = Math.round(renderedWidth / aspectRatio);
      }
    } else if (aspectRatio) {
      renderedHeight = Math.round(renderedWidth / aspectRatio);
      if (origHeight && renderedHeight > origHeight) {
        renderedHeight = origHeight;
        renderedWidth = Math.round(renderedHeight * aspectRatio);
      }
    }

    const handleLoad = useCallback(
      (ev: React.SyntheticEvent<HTMLImageElement>) => {
        if (onLoad) {
          onLoad(ev);
        }
      },
      []
    );

    const handleError = useCallback(
      (ev: React.SyntheticEvent<HTMLImageElement>) => {
        if (onError) {
          onError(ev);
        }
      },
      []
    );

    const imageSrcSet = !srcSet
      ? makeSrcset(
          src,
          renderedWidth,
          renderedHeight,
          undefined,
          srcSetScale,
          useResponsivePNG
        )
      : srcSet;

    return (
      <img
        className={cx}
        data-component={slugName}
        ref={ref}
        src={src}
        onLoad={handleLoad}
        onError={handleError}
        srcSet={imageSrcSet}
        sizes={sizes}
        loading={isLazy ? 'lazy' : 'eager'}
        alt={altText}
        decoding={decoding}
        // eslint-disable-next-line react/no-unknown-property
        fetchpriority={fetchpriority}
      />
    );
  }
);
